import type { FragmentOf } from "@/data-access/graphql";
import { graphql } from "@/data-access/graphql";
import { NewsFeedFragment } from "../fragments/news-feeds-fragments";

/** Feed item for nav/switcher UI: id, name and optional logo (aliased as image). */
export type NewsFeedItem = FragmentOf<typeof NewsFeedFragment>;

/**
 * Type for news feed terms (key terms with descriptions)
 */
export type NewsFeedTermsType = {
  term: string;
  description: string;
};

/**
 * Query for fetching all news feeds
 * This query is used across multiple parts of the application
 * for displaying feed lists in sidebars and navigation
 */
export const GetAllNewsFeeds = graphql(
  `
    query getAllNewsFeeds {
      feeds(ordering: [{order: ASC}, {name: ASC}]) {
        ...NewsFeedFragment
        terms {
          term
          description
        }
        knownTags {
          tag
        }
      }
    }
  `,
  [NewsFeedFragment],
);

export const CreateFeed = graphql(`
  mutation createFeed($data: FeedCreateInput!) {
    createFeed(data: $data) {
      ... on FeedType {
        __typename
        id
        name
        terms {
          term
          description
          id
        }
        knownTags {
          id
          tag
        }
      }
    }
  }
`);

export const UpdateFeed = graphql(`
  mutation updateFeed($data: FeedUpdateInput!, $feedId: Int!) {
    updateFeed(data: $data, id: $feedId) {
      ... on FeedType {
        __typename
        id
        name
        terms {
          id
          term
          description
        }
        knownTags {
          id
          tag
        }
      }
    }
  }
`);

export const DeleteFeed = graphql(`
  mutation deleteFeed($feedId: ID!, $accepted: Boolean!) {
    deleteFeed(feedId: $feedId, accepted: $accepted) {
      ... on SuccessResponse {
        __typename
        message
      }
      ... on OperationInfo {
        __typename
        messages {
          message
        }
      }
    }
  }
`);

/**
 * Query for fetching feed total stats
 */
export const GetFeedMetricsQuery = graphql(`
  query getFeedMetrics($filters: FeedArticleProjectionFilter!) {
    newsFeedStats(filters: $filters) {
      grossImpact
      features
      grossNegativeImpact
      grossNeutralImpact
      grossPositiveImpact
      headlines
      netImpactScore
      netSentiment
      passingMentions
      readership
      socialEngagement
      tier1
      tier2
      tier3
      totalMentions
      averageArticleImpact
      averageImpactStrength
      negativeMentions
      neutralMentions
      positiveMentions
    }
  }
`);

/**
 * Mutation to export feed article projections as CSV. Returns an array of
 * CSVExportResponse (presigned URL + expiry) or OperationInfo (errors).
 */
export const ExportFeedArticlesInCSVFormat = graphql(`
  mutation ExportFeedArticlesInCSVFormat($input: ExportFeedArticleProjectionsCsvInput!) {
    exportFeedArticleProjectionsCsv(input: $input) {
      ... on CSVExportResponse {
        __typename
        expiresInHours
        message
        url
      }
      ... on OperationInfo {
        __typename
        messages {
          code
          field
          kind
          message
        }
      }
    }
  }
`);
